import { useState, useEffect, useCallback, useRef } from "react";
import { axiosGet, axiosPost } from "@/services/axiosService";
import { useContext } from "react";
import { BotContext } from "@/pages/cbb-builder/store";
import { useDispatch } from "react-redux";
import { userSlice } from "../../../redux/User";
import { useQueryClient } from "@tanstack/react-query";

interface DocumentStatus {
  id: string;
  title: string;
  status: string;
  lastSyncTime: number;
  characterCount: number;
  size: number;
  mime?: string;
  isPdfAsync?: boolean;
  dataSource?:
    | "documents"
    | "googlepicker"
    | "notion"
    | "confluence"
    | "salesforce";
}

interface UrlStatus {
  id: string;
  url: string;
  status: string;
  lastSyncTime: number;
  characterCount: number;
  title?: string;
}

interface PollingStatusResponse {
  documents?: DocumentStatus[];
  urls?: UrlStatus[];
  googlepicker?: DocumentStatus[];
  notion?: DocumentStatus[];
  confluence?: DocumentStatus[];
  salesforce?: DocumentStatus[];
  characterUsed?: number;
}

interface PollingOptions {
  enabled: boolean;
  interval: number;
  maxRetries: number;
  onStatusChange?: (data: PollingStatusResponse) => void;
  onError?: (error: any) => void;
  setCharacterUsed?: (characterCount: number) => void;
  creditId?: any;
}

export function useDocumentStatusPolling(
  botCustomId: string,
  botData: any = null,
  options: Partial<PollingOptions> = {},
) {
  const {
    enabled = true,
    interval = 15000,
    maxRetries = 3,
    onStatusChange,
    onError,
    setCharacterUsed,
    creditId,
  } = options;
  const [store, storeDispatch] = useContext(BotContext);
  // Remove these individual state variables since we're using the callback
  // const [documents, setDocuments] = useState<DocumentStatus[]>([]);
  // const [urls, setUrls] = useState<UrlStatus[]>([]);
  // const [googlepicker, setGooglepicker] = useState<DocumentStatus[]>([]);
  // const [notion, setNotion] = useState<DocumentStatus[]>([]);
  // const [confluence, setConfluence] = useState<DocumentStatus[]>([]);
  // const [salesforce, setSalesforce] = useState<DocumentStatus[]>([]);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditUsed, setCreditUsed] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const lastUpdateRef = useRef<number>(0);
  const isPollingRef = useRef(false);
  const isResyncRef = useRef(false);
  const lastBotDataRef = useRef<string>("");

  // Get all document-type items that need tracking across all data sources
  const getDocumentIdsToTrack = useCallback(() => {
    const allDocumentIds: { id: string; source: string }[] = [];

    // Regular documents
    if (botData?.aiSource?.documents) {
      const documentsInProgress = botData.aiSource.documents.filter((doc) => {
        const isInProgress =
          doc.status === "In Progress" ||
          doc.status === "Processing" ||
          (doc.isPdfAsync && doc.status === "In Progress");

        // if (isInProgress) {
        //   console.log(
        //     `Document ${doc.id} (${doc.title}) is in progress with status: ${doc.status}`,
        //   );
        // }

        return isInProgress;
      });

      allDocumentIds.push(
        ...documentsInProgress
          .map((doc) => ({
            id: doc.id,
            source: "documents",
          }))
          .filter((item) => item.id),
      );
    }

    // Google Picker documents
    if (botData?.aiSource?.googlepicker) {
      const googlePickerInProgress = botData.aiSource.googlepicker.filter(
        (doc) => {
          const isInProgress =
            doc.status === "In Progress" || doc.status === "Processing";

          // if (isInProgress) {
          //   console.log(
          //     `Google Picker document ${doc.id} (${doc.title}) is in progress with status: ${doc.status}`,
          //   );
          // }

          return isInProgress;
        },
      );

      allDocumentIds.push(
        ...googlePickerInProgress
          .map((doc) => ({
            // Prefer the per-row googlePickerId; `id` is the Google config id
            // and is identical for every file from the same Drive account.
            id: doc.googlePickerId || doc.id,
            source: "googlepicker",
          }))
          .filter((item) => item.id),
      );
    }

    // Notion documents
    if (botData?.aiSource?.notion) {
      const notionInProgress = botData.aiSource.notion.filter((doc) => {
        const isInProgress =
          doc.status === "In Progress" || doc.status === "Processing";

        // if (isInProgress) {
        //   console.log(
        //     `Notion document ${doc.id} (${doc.title}) is in progress with status: ${doc.status}`,
        //   );
        // }

        return isInProgress;
      });

      allDocumentIds.push(
        ...notionInProgress
          .map((doc) => ({
            id: doc.id,
            source: "notion",
          }))
          .filter((item) => item.id),
      );
    }

    // Confluence documents
    if (botData?.aiSource?.confluence) {
      const confluenceInProgress = botData.aiSource.confluence.filter((doc) => {
        const isInProgress =
          doc.status === "In Progress" || doc.status === "Processing";

        // if (isInProgress) {
        //   console.log(
        //     `Confluence document ${doc.id} (${doc.title}) is in progress with status: ${doc.status}`,
        //   );
        // }

        return isInProgress;
      });

      allDocumentIds.push(
        ...confluenceInProgress
          .map((doc) => ({
            id: doc.id,
            source: "confluence",
          }))
          .filter((item) => item.id),
      );
    }

    // Salesforce documents
    if (botData?.aiSource?.salesforce) {
      const salesforceInProgress = botData.aiSource.salesforce.filter((doc) => {
        const isInProgress =
          doc.status === "In Progress" || doc.status === "Processing";

        // if (isInProgress) {
        //   console.log(
        //     `Salesforce document ${doc.id} (${doc.title}) is in progress with status: ${doc.status}`,
        //   );
        // }

        return isInProgress;
      });

      allDocumentIds.push(
        ...salesforceInProgress
          .map((doc) => ({
            id: doc.id,
            source: "salesforce",
          }))
          .filter((item) => item.id),
      );
    }

    // console.log(
    //   `Found ${allDocumentIds.length} total documents in progress across all sources:`,
    //   allDocumentIds,
    // );

    return allDocumentIds;
  }, [botData?.aiSource]);

  // Extract URLs that need tracking from botData (using URLs as identifiers)
  const getUrlsToTrack = useCallback(() => {
    if (!botData?.aiSource?.urls) {
      // console.log("No URLs found in botData");
      return [];
    }

    const urlsInProgress = botData.aiSource.urls.filter((url) => {
      const isInProgress =
        url.status === "In Progress" ||
        url.status === "Processing" ||
        url.status === "Re-syncing";

      // if (isInProgress) {
      //   console.log(`URL ${url.url} is in progress with status: ${url.status}`);
      // }

      return isInProgress;
    });

    const urls = urlsInProgress.map((url) => url.url).filter(Boolean);
    // console.log(`Found ${urls.length} URLs in progress:`, urls);

    return urls;
  }, [botData?.aiSource?.urls]);

  // Poll documents using existing route - now supports multiple data sources
  const pollDocuments = useCallback(
    async (documentItems: { id: string; source: string }[]) => {
      if (documentItems.length === 0)
        return {
          documents: [],
          googlepicker: [],
          notion: [],
          confluence: [],
          salesforce: [],
        };

      try {
        const documentIds = documentItems.map((item) => item.id);
        const params = `?documentIds=${documentIds.join(",")}`;
        // console.log(
        //   `Polling document status for bot ${botCustomId} with documents: ${documentIds.join(", ")}`,
        // );

        const doc = creditId
          ? `/ai/documents/status/${botCustomId}/${creditId}${params}`
          : `/ai/documents/status/${botCustomId}${params}`;

        const response = await axiosGet(doc);

        if (response.data.success) {
          // Use the grouped response if available, otherwise fall back to the flat array
          if (response.data.data.grouped) {
            // console.log(
            //   "Using grouped document response:",
            //   response.data.data.grouped,
            // );

            // Update character count if provided by API
            const characterCount = response.data.data?.characterUsed;
            if (creditId) {
              queryClient.invalidateQueries({
                queryKey: ["getCredits", creditId],
                exact: true,
              });
              // dispatch(
              //   userSlice.actions.setCreditUsed(
              //     response.data.data?.curCreditUsed,
              //   ),
              // );
            } else if (characterCount !== undefined) {
              setCharacterUsed?.(characterCount);
            }

            return response.data.data.grouped;
          } else {
            // Fallback to manual grouping for backward compatibility
            const allDocuments = response.data.data.documents || [];

            const groupedDocuments = {
              documents: [],
              googlepicker: [],
              notion: [],
              confluence: [],
              salesforce: [],
            };

            allDocuments.forEach((doc) => {
              const originalSource =
                documentItems.find((item) => item.id === doc.id)?.source ||
                "documents";
              if (groupedDocuments[originalSource]) {
                groupedDocuments[originalSource].push({
                  ...doc,
                  dataSource: originalSource,
                });
              }
            });
            if (creditId) {
              dispatch(
                userSlice.actions.setCreditUsed(
                  response.data.data?.curCreditUsed,
                ),
              );
            }

            // console.log(
            //   "Manually grouped documents by source:",
            //   groupedDocuments,
            // );
            return groupedDocuments;
          }
        } else {
          throw new Error(
            response.data.message || "Failed to fetch document status",
          );
        }
      } catch (err) {
        console.error("Error polling documents:", err);
        throw err;
      }
    },
    [botCustomId, creditId, queryClient, setCharacterUsed, dispatch],
  );

  // Poll URLs using URL strings as identifiers
  const pollUrls = useCallback(async () => {
    try {
      const url = creditId
        ? `/ai/urls/status/${botCustomId}/${creditId}`
        : `/ai/urls/status/${botCustomId}`;
      const response = await axiosPost(url);
      if (creditId) setCreditUsed(response.data.curCreditUsed);
      // dispatch(userSlice.actions.setCreditUsed(response.data.curCreditUsed));

      queryClient.invalidateQueries({
        queryKey: ["getCredits", creditId],
        exact: true,
      });
      return response.data.urlData;
    } catch (err) {
      console.error("Error polling URLs:", err);
      console.warn("URL status polling not available, skipping...");
      return [];
    }
  }, [botCustomId]);

  const pollStatus = useCallback(async () => {
    const currentDocumentItems = getDocumentIdsToTrack();
    const currentUrls = getUrlsToTrack();

    if (isPollingRef.current) {
      // console.log("Skipping poll: already polling in progress");
      return;
    }

    if (!botCustomId) {
      // console.log("Skipping poll: no botCustomId provided");
      return;
    }

    if (currentDocumentItems.length === 0 && currentUrls.length === 0) {
      // console.log(
      //   "Skipping poll: no documents or URLs in progress, stopping polling",
      // );
      setIsPolling(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    isPollingRef.current = true;

    try {
      // Poll documents and URLs separately using existing routes
      if (isResyncRef.current) {
        isResyncRef.current = false;
        return;
      }
      const [groupedDocuments, urlData]: any = await Promise.all([
        pollDocuments(currentDocumentItems),
        pollUrls(),
      ]);

      if (urlData?.length) {
        storeDispatch({
          type: "update-aiSource-url",
          urlData,
        });
      }

      const currentTime = Date.now();

      // console.log(`Received document updates:`, groupedDocuments);
      // console.log(`Received ${newUrls.length} URL updates`);

      // REMOVED all the setDocuments, setGooglepicker, etc. calls
      // We only use the callback now to prevent duplicate updates

      // Always trigger callback if we have items to update
      if (currentDocumentItems.length > 0 || currentUrls.length > 0) {
        lastUpdateRef.current = currentTime;

        if (onStatusChange) {
          setTimeout(() => {
            onStatusChange({
              documents: groupedDocuments.documents || [],
              googlepicker: groupedDocuments.googlepicker || [],
              notion: groupedDocuments.notion || [],
              confluence: groupedDocuments.confluence || [],
              salesforce: groupedDocuments.salesforce || [],
              characterUsed: groupedDocuments.characterUsed ?? 0,
            });
          }, 0);
        }

        const polledDocs = [
          ...(groupedDocuments.documents || []),
          ...(groupedDocuments.googlepicker || []),
          ...(groupedDocuments.notion || []),
          ...(groupedDocuments.confluence || []),
          ...(groupedDocuments.salesforce || []),
        ];

        const stillPending = polledDocs.some(
          (doc: any) =>
            doc?.status === "In Progress" ||
            doc?.status === "Processing" ||
            doc?.status === "Re-syncing",
        );

        // Stop as soon as the API itself reports no document is pending.
        // Waiting on botData to come back around leaves the interval running
        // when the consumer's state update is a no-op, which spins forever.
        if (
          polledDocs.length > 0 &&
          !stillPending &&
          currentUrls.length === 0
        ) {
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }

      setError(null);
      retryCountRef.current = 0;
    } catch (err: any) {
      // console.error("Polling error:", err);
      retryCountRef.current++;

      if (retryCountRef.current >= maxRetries) {
        const errorMessage = err.message || "Failed to fetch status";
        setError(errorMessage);
        if (onError) {
          onError(err);
        }
        stopPolling();
        console.error(`Polling stopped after ${maxRetries} failed attempts`);
      } else {
        console.warn(`Polling retry ${retryCountRef.current}/${maxRetries}`);
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [
    botCustomId,
    maxRetries,
    onStatusChange,
    onError,
    getDocumentIdsToTrack,
    getUrlsToTrack,
    pollDocuments,
    pollUrls,
    storeDispatch,
  ]);

  const startPolling = useCallback(() => {
    const currentDocumentItems = getDocumentIdsToTrack();
    const currentUrls = getUrlsToTrack();

    if (!enabled) {
      // console.log("Cannot start polling: disabled");
      return;
    }

    if (!botCustomId) {
      // console.log("Cannot start polling: no botCustomId");
      return;
    }

    if (currentDocumentItems.length === 0 && currentUrls.length === 0) {
      // console.log("Cannot start polling: no documents or URLs in progress");
      return;
    }

    if (isPolling) {
      // console.log("Polling already active");
      return;
    }

    // console.log(
    //   `Starting status polling every ${interval}ms for ${currentDocumentItems.length} documents and ${currentUrls.length} URLs`,
    // );

    setIsPolling(true);
    setError(null);
    retryCountRef.current = 0;
    lastUpdateRef.current = Date.now();

    // Initial poll
    pollStatus();

    // Set up interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // console.log("Interval triggered, checking status...");
      pollStatus();
    }, interval);
  }, [
    enabled,
    botCustomId,
    interval,
    pollStatus,
    getDocumentIdsToTrack,
    getUrlsToTrack,
    isPolling,
  ]);

  const stopPolling = useCallback(() => {
    console.log("Stopping status polling");
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
    isResyncRef.current = true;
  }, []);

  const restartPolling = useCallback(() => {
    console.log("Restarting status polling");
    stopPolling();
    setTimeout(() => {
      startPolling();
    }, 100);
  }, [stopPolling, startPolling]);

  // Effect to handle data changes
  useEffect(() => {
    const currentDocumentItems = getDocumentIdsToTrack();
    const currentUrls = getUrlsToTrack();
    const hasItemsInProgress =
      currentDocumentItems.length > 0 || currentUrls.length > 0;
    const botDataSignature = JSON.stringify({
      documents: botData?.aiSource?.documents || [],
      urls: botData?.aiSource?.urls || [],
      googlepicker: botData?.aiSource?.googlepicker || [],
      notion: botData?.aiSource?.notion || [],
      confluence: botData?.aiSource?.confluence || [],
      salesforce: botData?.aiSource?.salesforce || [],
    });

    // Only restart if the actual bot data changed significantly
    if (botDataSignature !== lastBotDataRef.current) {
      // console.log("Bot data changed, evaluating polling needs");
      lastBotDataRef.current = botDataSignature;

      if (enabled && botCustomId && hasItemsInProgress && !isPolling) {
        // console.log("Starting polling due to items in progress");
        startPolling();
      } else if (
        (!hasItemsInProgress || !enabled || !botCustomId) &&
        isPolling
      ) {
        // console.log("Stopping polling - no items in progress or disabled");
        stopPolling();
      }
    }
  }, [
    botData?.aiSource,
    enabled,
    botCustomId,
    isPolling,
    startPolling,
    stopPolling,
    getDocumentIdsToTrack,
    getUrlsToTrack,
  ]);

  // Auto-start polling when conditions are met
  useEffect(() => {
    const currentDocumentItems = getDocumentIdsToTrack();
    const currentUrls = getUrlsToTrack();
    const hasItemsInProgress =
      currentDocumentItems.length > 0 || currentUrls.length > 0;

    if (enabled && botCustomId && hasItemsInProgress && !isPolling) {
      // console.log("Auto-starting polling on component mount/conditions met");
      startPolling();
    }
  }, [enabled, botCustomId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // console.log("Cleaning up status polling on unmount");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, []);

  return {
    // Remove individual data arrays since we're using callback only
    // documents,
    // urls,
    // googlepicker,
    // notion,
    // confluence,
    // salesforce,
    isPolling,
    error,
    startPolling,
    stopPolling,
    restartPolling,
    creditUsed,
  };
}
